import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, MessageSquareMore, Phone, MessageCircle, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";

const TYPES = { public: "Public", prive: "Privé", "franco-arabe": "Franco-arabe", communautaire: "Communautaire", autre: "Autre" };
const ORGS = { simple: "Classe simple", multigrade: "Multigrade", double_flux: "Double flux" };
const ATTACH = { pending: "En attente du directeur", confirmed: "Confirmé", refused: "Refusé par le directeur", admin: "École sans directeur — à valider par l'admin" };

function Section({ title, rows }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-[#1F7A63] mb-2">{title}</div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.filter(([, v]) => v !== undefined).map(([k, v]) => (
          <div key={k}><dt className="text-xs text-[#5C736A]">{k}</dt><dd className="font-medium break-words">{v || "—"}</dd></div>
        ))}
      </dl>
    </div>
  );
}

function ActionBox({ mode, onCancel, onSubmit, required }) {
  const [text, setText] = useState("");
  const isReject = mode === "reject";
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#F6F8F9] p-4 space-y-3" data-testid={`${mode}-box`}>
      <Label>{isReject ? `Motif du refus${required ? " (obligatoire)" : " (facultatif)"}` : "Informations à demander"}</Label>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={isReject ? "ex : informations incomplètes" : "ex : merci de préciser le nombre exact de classes"} data-testid={`${mode}-text`} />
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={() => onSubmit(text)} className={isReject ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#1F7A63] hover:bg-[#165c4b] text-white"} data-testid={`${mode}-submit`}>
          {isReject ? "Confirmer le refus" : "Envoyer la demande"}
        </Button>
      </div>
    </div>
  );
}

export default function RegistrationDetail({ id, onClose, onChanged }) {
  const [reg, setReg] = useState(null);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linkSchool, setLinkSchool] = useState(null);
  const [settings, setSettings] = useState({ refusal_reason_required: false });

  const load = () => api.get(`/admin/registrations/${id}`).then(({ data }) => setReg(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => {
    if (!id) return;
    setReg(null); setMode(null); setLinkSchool(null);
    load();
    api.get("/admin/registration-settings").then(({ data }) => setSettings(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(`/admin/registrations/${id}/${path}`, body); toast.success(msg); setMode(null); await load(); onChanged?.(); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const confirmAttach = async () => {
    try { await api.post(`/registrations/${id}/attachment/confirm`); toast.success("Rattachement confirmé"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const s = reg?.school || {};
  const phone = (reg?.telephone || "").replace(/\D/g, "");
  const wa = phone ? `https://wa.me/${phone.length === 9 ? "221" + phone : phone}` : null;
  const pending = reg?.status === "pending";
  const freeSimilar = (reg?.similar_schools || []).filter((x) => !x.directeur_id);

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="registration-detail">
        {!reg ? <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#1F7A63]" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                {reg.name} <StatusBadge status={reg.status} />
              </DialogTitle>
              <div className="text-xs text-[#5C736A]">{reg.numero} · {reg.profil === "directeur" ? "Directeur" : "Enseignant"} · reçue le {new Date(reg.created_at).toLocaleString("fr-FR")}</div>
            </DialogHeader>
            <div className="space-y-5">
              <Section title="Informations personnelles" rows={[["Prénom et nom", reg.name], ["Sexe", reg.sexe === "F" ? "Féminin" : "Masculin"], ["Téléphone", reg.telephone], ["E-mail", reg.email], ...(reg.profil === "directeur" ? [["Fonction", reg.fonction]] : []), ["Identifiant", reg.username], ["E-mail de récupération", reg.recovery_email]]} />
              <Section title="Établissement" rows={[["École", s.nom], ["Type", TYPES[s.type_etablissement]], ["IA", s.ia], ["IEF", s.ief], ["Région", s.region], ["Département", s.departement], ["Commune", s.commune], ["Localité", s.localite], ["Classes", s.nb_classes?.toString()], ["Enseignants", s.nb_enseignants?.toString()], ["Élèves (approx.)", s.nb_eleves?.toString()], ["Téléphone école", s.telephone]]} />
              {s.logo_base64 && <img src={s.logo_base64} alt="Logo" className="w-16 h-16 object-contain border rounded-lg" />}
              {reg.profil === "maitre" && (
                <Section title="Organisation pédagogique" rows={[["Niveaux", (reg.niveaux || []).join(", ")], ["Organisation", ORGS[reg.organisation]], ...(reg.organisation === "double_flux" ? [["Flux", reg.flux || "A, B"]] : []), ["Rattachement", reg.attach ? `${reg.attach.school_nom} — ${ATTACH[reg.attach.status]}` : "Enseignant autonome (nouvelle école)"]]} />
              )}
              {reg.info_requests?.length > 0 && (
                <div className="rounded-xl bg-[#FFF7D6] p-3 text-sm space-y-1">
                  <div className="font-semibold text-[#7A6100]">Informations demandées</div>
                  {reg.info_requests.map((r) => <div key={r.at}>« {r.message} » <span className="text-xs text-[#5C736A]">— {new Date(r.at).toLocaleDateString("fr-FR")}</span></div>)}
                </div>
              )}
              {reg.motif_refus && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">Motif du refus : {reg.motif_refus}</div>}
              {reg.result && <div className="rounded-xl bg-[#E8F2EF] p-3 text-sm text-[#1F7A63]">Résultat : {reg.result} · par {reg.decided_by}</div>}

              {pending && reg.profil === "directeur" && freeSimilar.length > 0 && (
                <div className="rounded-xl border border-[#E6B800] bg-[#FFFBEB] p-3 text-sm space-y-2" data-testid="similar-schools">
                  <div className="font-semibold">Une école portant ce nom existe déjà sans directeur. L&apos;associer plutôt que d&apos;en créer une nouvelle ?</div>
                  {freeSimilar.map((x) => (
                    <label key={x.id} className="flex items-center gap-2"><input type="radio" name="link" checked={linkSchool === x.id} onChange={() => setLinkSchool(x.id)} /> {x.nom} {x.ief ? `· ${x.ief}` : ""}</label>
                  ))}
                  <label className="flex items-center gap-2"><input type="radio" name="link" checked={!linkSchool} onChange={() => setLinkSchool(null)} /> Créer une nouvelle école</label>
                </div>
              )}

              {mode && <ActionBox mode={mode} required={settings.refusal_reason_required} onCancel={() => setMode(null)}
                onSubmit={(t) => (mode === "reject" ? act("reject", { motif: t }, "Demande refusée") : act("request-info", { message: t }, "Demande d'informations enregistrée"))} />}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8F0]">
                {pending && (
                  <>
                    <Button disabled={busy} onClick={() => act("approve", { school_id: linkSchool }, "Compte approuvé et activé")} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="reg-approve-btn"><Check className="w-4 h-4 mr-1" /> Approuver</Button>
                    <Button disabled={busy} variant="outline" onClick={() => setMode("reject")} className="text-red-600 border-red-200" data-testid="reg-reject-btn"><X className="w-4 h-4 mr-1" /> Refuser</Button>
                    <Button disabled={busy} variant="outline" onClick={() => setMode("info")} data-testid="reg-info-btn"><MessageSquareMore className="w-4 h-4 mr-1" /> Demander des informations</Button>
                    {reg.attach?.status === "pending" || reg.attach?.status === "admin" ? (
                      <Button variant="outline" onClick={confirmAttach} data-testid="reg-confirm-attach-btn"><Link2 className="w-4 h-4 mr-1" /> Confirmer le rattachement</Button>
                    ) : null}
                  </>
                )}
                {phone && <a href={`tel:${reg.telephone}`}><Button variant="outline" data-testid="reg-call-btn"><Phone className="w-4 h-4 mr-1" /> Appeler</Button></a>}
                {wa && <a href={wa} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="reg-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> Contacter le client</Button></a>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
